import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import axios from 'axios';
import { 
  LogOut, 
  Video, 
  FileText, 
  Calendar, 
  Bell,
  Play,
  ExternalLink,
  Download,
  MessageSquare,
  Mail
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cloudinary raw PDFs need fl_attachment to force a proper download
const getPdfDownloadUrl = (url) => {
  if (!url) return url;
  if (url.includes('/upload/') && !url.includes('fl_attachment')) {
    return url.replace('/upload/', '/upload/fl_attachment/');
  }
  return url;
};

const handleDownloadPdf = (url, title) => {
  if (!url) return;
  const link = document.createElement('a');
  link.href = getPdfDownloadUrl(url);
  link.setAttribute('download', `${title || 'assignment'}.pdf`);
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [student, setStudent] = useState(null);
  const [recordedSessions, setRecordedSessions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [classSchedule, setClassSchedule] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [readNotifications, setReadNotifications] = useState(new Set());
  const [readMessages, setReadMessages] = useState(new Set());

  useEffect(() => {
    const studentData = sessionStorage.getItem('student');
    if (!studentData) {
      navigate('/student/login');
      return;
    }
    
    const parsedStudent = JSON.parse(studentData);
    setStudent(parsedStudent);
    // Use batch_code if available, otherwise fallback to batch_name
    fetchData(parsedStudent.batch_code || parsedStudent.batch_name, parsedStudent.id);
  }, [navigate]);

  const fetchData = async (batchCode, studentId) => {
    try {
      // Use batch_code parameter for filtering
      const [sessionsRes, assignmentsRes, scheduleRes, notificationsRes, dmRes] = await Promise.all([
        axios.get(`${API}/recorded-sessions?batch_code=${batchCode}`),
        axios.get(`${API}/assignments?batch_code=${batchCode}`),
        axios.get(`${API}/class-schedule?batch_code=${batchCode}`),
        axios.get(`${API}/notifications?batch_code=${batchCode}`),
        axios.get(`${API}/direct-messages/student/${studentId}`)
      ]);
      
      setRecordedSessions(sessionsRes.data);
      setAssignments(assignmentsRes.data);
      setClassSchedule(scheduleRes.data);
      setNotifications(notificationsRes.data);
      setDirectMessages(dmRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('student');
    navigate('/student/login');
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully"
    });
  };

  const markAsRead = (notificationId) => {
    setReadNotifications(prev => new Set([...prev, notificationId]));
  };

  const markMessageAsRead = async (messageId) => {
    if (!readMessages.has(messageId)) {
      setReadMessages(prev => new Set([...prev, messageId]));
      try {
        await axios.patch(`${API}/direct-messages/${messageId}/read`);
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const unreadCount = notifications.filter(n => !readNotifications.has(n.id)).length;
  const unreadDMCount = directMessages.filter(m => !m.is_read && !readMessages.has(m.id)).length;

  if (!student) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-700 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-7 w-7 object-contain" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">Student Portal</span>
                <p className="text-xs text-slate-300">{student.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-white/20 text-white border-white/30">
                {student.batch_name}
              </Badge>
              <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/10">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Card */}
        <Card className="mb-6 bg-gradient-to-r from-slate-900 to-slate-800 text-white border-none shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold mb-2">Welcome back, {student.name}! 👋</h2>
            <p className="text-slate-300">
              Batch: {student.batch_name} • {student.institution}
            </p>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs defaultValue="sessions" className="space-y-6">
          <TabsList className="grid w-full max-w-4xl grid-cols-5 bg-gradient-to-r from-slate-100 to-slate-50">
            <TabsTrigger value="sessions" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="sessions-tab">
              <Video className="h-4 w-4 mr-2" />
              Sessions
            </TabsTrigger>
            <TabsTrigger value="assignments" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="assignments-tab">
              <FileText className="h-4 w-4 mr-2" />
              Assignments
            </TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="schedule-tab">
              <Calendar className="h-4 w-4 mr-2" />
              Schedule
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white relative" data-testid="messages-tab">
              <MessageSquare className="h-4 w-4 mr-2" />
              Messages
              {unreadDMCount > 0 && (
                <Badge className="ml-2 bg-blue-500 text-white text-xs px-1.5 py-0 min-w-[1.25rem]">
                  {unreadDMCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white relative" data-testid="notifications-tab">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
              {unreadCount > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0 min-w-[1.25rem]">
                  {unreadCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Recorded Sessions Tab */}
          <TabsContent value="sessions">
            <Card>
              <CardHeader>
                <CardTitle>Recorded Sessions</CardTitle>
                <CardDescription>Watch previously recorded class sessions</CardDescription>
              </CardHeader>
              <CardContent>
                {recordedSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <Video className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No recorded sessions available yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recordedSessions.map((session) => (
                      <Card key={session.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className="w-48 h-28 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Play className="h-12 w-12 text-white" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-slate-900 mb-1">{session.title}</h3>
                              <p className="text-sm text-slate-600 mb-2">{session.description}</p>
                              <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                                <Badge variant="outline">{session.batch_name}</Badge>
                                {session.duration && <span>Duration: {session.duration}</span>}
                                <span>Uploaded: {new Date(session.uploaded_at).toLocaleDateString()}</span>
                              </div>
                              <Button 
                                size="sm" 
                                className="bg-slate-900 hover:bg-slate-800"
                                onClick={() => window.open(session.video_url, '_blank')}
                              >
                                <Play className="h-4 w-4 mr-2" />
                                Watch Session
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments">
            <Card>
              <CardHeader>
                <CardTitle>Assignments</CardTitle>
                <CardDescription>View and download your course assignments</CardDescription>
              </CardHeader>
              <CardContent>
                {assignments.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No assignments available yet</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {assignments.map((assignment) => (
                      <Card key={assignment.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText className="h-6 w-6 text-slate-700" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-900">{assignment.title}</h3>
                              <p className="text-sm text-slate-600 mt-1">{assignment.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <Badge variant="outline" className="text-xs">{assignment.batch_name}</Badge>
                            <div className="flex items-center gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                data-testid={`view-pdf-btn-${assignment.id}`}
                                onClick={() => window.open(assignment.file_url, '_blank', 'noopener,noreferrer')}
                                disabled={!assignment.file_url}
                              >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-slate-900 hover:bg-slate-800 text-white"
                                data-testid={`download-pdf-btn-${assignment.id}`}
                                onClick={() => handleDownloadPdf(assignment.file_url, assignment.title)}
                                disabled={!assignment.file_url}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Class Schedule Tab */}
          <TabsContent value="schedule">
            <Card>
              <CardHeader>
                <CardTitle>Class Schedule</CardTitle>
                <CardDescription>Upcoming live classes and meetings</CardDescription>
              </CardHeader>
              <CardContent>
                {classSchedule.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No scheduled classes yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {classSchedule.map((schedule) => (
                      <Card key={schedule.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-slate-900 mb-1">{schedule.title}</h3>
                              <p className="text-sm text-slate-600 mb-2">{schedule.description}</p>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  <span>{new Date(schedule.scheduled_date).toLocaleDateString('en-US', { 
                                    weekday: 'short',
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}</span>
                                </div>
                                <span>•</span>
                                <span>{schedule.scheduled_time}</span>
                                <span>•</span>
                                <Badge variant="outline" className="text-xs">{schedule.batch_name}</Badge>
                              </div>
                            </div>
                            <Button 
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => window.open(schedule.meet_link, '_blank')}
                              data-testid={`join-class-${schedule.id}`}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Join Class
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Direct Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Messages from Admin
                </CardTitle>
                <CardDescription>Personal messages and communications from the admin team</CardDescription>
              </CardHeader>
              <CardContent>
                {directMessages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No messages yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {directMessages.map((message) => {
                      const isRead = message.is_read || readMessages.has(message.id);
                      return (
                        <Card 
                          key={message.id} 
                          className={`border-slate-200 cursor-pointer transition-all ${
                            isRead ? 'bg-slate-50' : 'bg-white border-l-4 border-l-blue-500'
                          }`}
                          onClick={() => markMessageAsRead(message.id)}
                          data-testid={`message-${message.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className={`font-semibold ${isRead ? 'text-slate-700' : 'text-slate-900'}`}>
                                    {message.title}
                                  </h3>
                                  {!isRead && (
                                    <Badge className="bg-blue-500 text-white text-xs">New</Badge>
                                  )}
                                </div>
                                <p className={`text-sm mb-2 ${isRead ? 'text-slate-500' : 'text-slate-600'}`}>
                                  {message.message}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                  <span>{new Date(message.created_at).toLocaleDateString()}</span>
                                  <span>•</span>
                                  <span>{new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notifications</CardTitle>
                <CardDescription>Important announcements and updates</CardDescription>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No notifications yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((notification) => {
                      const isRead = readNotifications.has(notification.id);
                      return (
                        <Card 
                          key={notification.id} 
                          className={`border-slate-200 cursor-pointer transition-all ${
                            isRead ? 'bg-slate-50' : 'bg-white border-l-4 border-l-blue-500'
                          }`}
                          onClick={() => markAsRead(notification.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className={`font-semibold ${isRead ? 'text-slate-700' : 'text-slate-900'}`}>
                                    {notification.title}
                                  </h3>
                                  {!isRead && (
                                    <Badge className="bg-blue-500 text-white text-xs">New</Badge>
                                  )}
                                </div>
                                <p className={`text-sm mb-2 ${isRead ? 'text-slate-500' : 'text-slate-600'}`}>
                                  {notification.message}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                  <span>{new Date(notification.created_at).toLocaleDateString()}</span>
                                  <span>•</span>
                                  <Badge variant="outline" className="text-xs">{notification.batch_name}</Badge>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StudentDashboard;
